<template>
  <div :aria-hidden="isMobile" class="hidden md:block">
    <ContainerDealSticker v-if="isFlashSalesOnPDPEnabled" />
    <ContainerDealTag />
    <div v-if="isLpoActive" class="flex items-start justify-between gap-8">
      <ContainerProductTitles />
      <ContainerFavoritesToggle is-rounded />
    </div>
    <ContainerProductTitles v-else />
    <ContainerRatingInfo />
    <div v-if="!isLpoActive" class="mt-24 flex flex-wrap items-center gap-12">
      <ContainerPrice />
      <div class="flex min-w-0 grow items-center gap-12">
        <ContainerAddToCart
          class="min-w-[164px] max-w-[256px] grow"
          data-id="product-page-buy-button-desktop"
          data-qa="product-page-buy-button-desktop"
        />
        <ContainerFavoritesToggle class="shrink-0" />
      </div>
    </div>
    <template v-else>
      <div class="mt-24">
        <ContainerPrice />
      </div>
      <div class="mt-16 flex gap-12">
        <PdpErrorBoundary v-if="hasPickers">
          <ErrorOwners :owners="['bot-squad-acquisition-front']">
            <LpoGoogleShoppingConfigureCta class="flex-1" />
          </ErrorOwners>
        </PdpErrorBoundary>
        <ContainerAddToCart
          class="min-w-0 flex-1"
          data-id="product-page-buy-button-desktop"
          data-qa="product-page-buy-button-desktop"
        />
      </div>
    </template>
    <MobilePlanSubsidy
      v-if="isFromGoogleShopping && selectedMobilePlan"
      :mobile-plan="selectedMobilePlan"
    />
    <TelcoCalculatorAdvertisement
      v-if="
        isUS &&
        product &&
        selectedOffer?.price &&
        product.tracking.categoryId === ProductCategory.SMARTPHONE
      "
      :price="selectedOffer.price"
    >
      <template #icons>
        <USTelcoCalculatorAdvertisementIcons />
      </template>
    </TelcoCalculatorAdvertisement>
  </div>
  <UnitSold
    class="hidden md:flex mb-8 !w-fit"
    :nb-unit="product?.nbUnitSold ?? null"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { MarketLink } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import ErrorOwners from '@backmarket/nuxt-module-logger/components/ErrorOwners.vue'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'

import ContainerAddToCart from '~/scopes/product-core/components/containers/ContainerAddToCart/ContainerAddToCart.vue'
import ContainerDealSticker from '~/scopes/product-core/components/containers/ContainerDealSticker/ContainerDealSticker.vue'
import ContainerDealTag from '~/scopes/product-core/components/containers/ContainerDealTag/ContainerDealTag.vue'
import ContainerPrice from '~/scopes/product-core/components/containers/ContainerPrice/ContainerPrice.vue'
import ContainerFavoritesToggle from '~/scopes/product-core/components/containers/ContainerProductSummary/components/ContainerFavoritesToggle/ContainerFavoritesToggle.vue'
import ContainerRatingInfo from '~/scopes/product-core/components/containers/ContainerProductSummary/components/ContainerRatingInfo/ContainerRatingInfo.vue'
import ContainerProductTitles from '~/scopes/product-core/components/containers/ContainerProductTitles/ContainerProductTitles.vue'
import LpoGoogleShoppingConfigureCta from '~/scopes/product-core/components/presentationals/LpoGoogleShoppingConfigureCta/LpoGoogleShoppingConfigureCta.vue'
import PdpErrorBoundary from '~/scopes/product-core/components/presentationals/PdpErrorBoundary/PdpErrorBoundary.vue'
import MobilePlanSubsidy from '~/scopes/product-core/components/presentationals/Price/components/MobilePlanSubsidy/MobilePlanSubsidy.vue'
import TelcoCalculatorAdvertisement from '~/scopes/product-core/components/presentationals/TelcoCalculatorAdvertisement/TelcoCalculatorAdvertisement.vue'
import UnitSold from '~/scopes/product-core/components/presentationals/UnitSold/UnitSold.vue'
import { useFetchPickers } from '~/scopes/product-core/composables/useFetch/useFetchPickers/useFetchPickers'
import { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'
import { useFlashSalesOnPDP } from '~/scopes/product-core/composables/useFlashSalesOnPDP/useFlashSalesOnPDP'
import { useGoogleShopping } from '~/scopes/product-core/composables/useGoogleShopping/useGoogleShopping'
import { useSelectedMobilePlan } from '~/scopes/product-core/composables/useSelectedMobilePlan/useSelectedMobilePlan'
import { ProductCategory } from '~/scopes/product-core/constants'
import USTelcoCalculatorAdvertisementIcons from '~/scopes/product-core/pages/ProductDetailPage/us/components/USTelcoCalculatorAdvertisementIcons/USTelcoCalculatorAdvertisementIcons.vue'

// @FIXME: potential hydration mismatch caused by client-only breakpoint detection
const breakpoint = useClientOnlyComputedBreakpoint()
const { isFromGoogleShopping, isLpoActive } = useGoogleShopping()
const { data: product } = useFetchProduct()
const { selectedOffer, data: pickersResponse } = useFetchPickers()

const hasPickers = computed(
  () => (pickersResponse.value?.pickerGroups?.length ?? 0) > 0,
)
const selectedMobilePlan = useSelectedMobilePlan()
const isFlashSalesOnPDPEnabled = useFlashSalesOnPDP()

const isMobile = computed(() => breakpoint.value < Breakpoint.MD)

const isUS = computed(() => {
  return product.value?.seo.marketsLinks.some(
    (market: MarketLink) => market.countryCode === MarketCountryCode.US,
  )
})
</script>
